import { Formik, Form, Field } from 'formik';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  DEFAULT_MANAGED_INTEGRATION_RELEASE_CHANNEL,
  useCreateIntegrationTemplateLogo,
  useCreateManagedIntegration,
  useDeleteIntegrationTemplateLogo,
  useEditManagedIntegration,
  useIntegrationTemplate,
  useManagedIntegration,
} from '@frontend/api';
import {
  FormControl,
  FormLabel,
  Input,
  Button,
  Checkbox,
  Select,
  ButtonGroup,
  Text,
  Flex,
  Box,
  DrawerBody,
  DrawerFooter,
  FormErrorMessage,
  Spinner,
  Center,
  VStack,
  Divider,
  PaperlessCubeIcon,
} from '@frontend/legacy-components';

import { MANAGED_INTEGRATION_LOGO_PLACEHOLDER } from 'constants/all';

import IntegrationTemplateSelector from 'modules/common/smartComponents/IntegrationTemplateSelector/IntegrationTemplateSelector';
import ManagedIntegrationCard from 'modules/settings/integrations/managedIntegrations/ManagedIntegrationCard';

import { isSuperUserOrGroupAdmin as getIsSuperUserOrGroupAdmin } from 'store/session/selectors';

import { notifySuccess, notifyError } from 'utils/notifyUtils';

import { IccSetupAwsCredentialsFields } from './IccSetupAwsCredentials';
import IccSetupReleaseChannel from './IccSetupReleaseChannel';
import LogoFormControl from './LogoFormControl';
import { managedIntegrationDrawer } from './data/actions';
import {
  isEditingManagedIntegration,
  getManagedIntegrationUuid,
  getIsSuperUser,
} from './data/reducer';
import type {
  FormData,
  ManagedIntegrationTemplate,
  ManagedIntegrationId,
} from './types';

const initialValues: Partial<FormData> = {
  category: '',
  description: '',
  author: '',
  supportContact: '',
  developedByPaperlessParts: false,
  integratedSoftwareName: '',
  name: '',
  showStatusIndicator: false,
  isPausable: false,
  isIccEnabled: false,
  isIccCompatible: false,
  awsCustomerAccessKey: '',
  awsCustomerSecretKey: '',
  releaseChannel: '',
};

const authoredByPaperlessValues: Partial<FormData> = {
  developedByPaperlessParts: true,
};

const resetAuthoredByPaperlessValues: Partial<FormData> = {
  developedByPaperlessParts: false,
};

const CreateManagedIntegrationTemplateForm = (): JSX.Element => {
  const { mutate: createTemplate } = useCreateManagedIntegration();

  const { mutateAsync: createLogo, isLoading: createLogoIsLoading } =
    useCreateIntegrationTemplateLogo();

  const { mutate: editTemplate, isLoading: editIsLoading } =
    useEditManagedIntegration();

  const { mutate: deleteLogo } = useDeleteIntegrationTemplateLogo();

  const [templateId, setTemplateId] = useState<ManagedIntegrationId>();
  const [noTemplate, setNoTemplate] = useState(false);
  const [formValues, setFormValues] =
    useState<Partial<FormData>>(initialValues);
  const [imageSrc, setImageSrc] = useState<string>('');
  const [isAuthoredByPaperlessParts, setIsAuthoredByPaperlessParts] =
    useState(false);
  const [fileBlob, setFileBlob] = useState<File>();
  const [emailInvalid, setEmailInvalid] = useState<boolean>(false);

  const template = useIntegrationTemplate(templateId, {
    queryOptions: { enabled: !!templateId, refetchOnWindowFocus: false },
  });
  const categoryOptions = [
    { label: 'ERP', value: 'ERP' },
    { label: 'CRM', value: 'CRM' },
    { label: 'Accounting', value: 'Accounting' },
    { label: 'Other', value: 'Other' },
  ];
  const dispatch = useDispatch();
  const isEditing = useSelector(isEditingManagedIntegration);
  const managedIntegrationUuid: string = useSelector(getManagedIntegrationUuid);
  const { data: managedIntegrationData } = useManagedIntegration(
    managedIntegrationUuid,
    {
      queryOptions: { enabled: !!managedIntegrationUuid },
    },
  );

  const isSuperUserOrGroupAdmin = useSelector(getIsSuperUserOrGroupAdmin);
  const isSuperUserOverride = useSelector(getIsSuperUser);
  const isSuperUser = isSuperUserOverride ?? isSuperUserOrGroupAdmin;

  const fieldsDisabled = !isEditing && isSuperUser && !noTemplate;

  const handleTemplateChange = (
    e:
      | {
          templateId: number;
          name: string;
        }
      | 'NO_TEMPLATE'
      | null,
  ): void => {
    setIsAuthoredByPaperlessParts(false);
    setNoTemplate(e === 'NO_TEMPLATE');
    if (e === 'NO_TEMPLATE') {
      setImageSrc('');
      setTemplateId(undefined);
      setFormValues({ ...initialValues, integrationTemplateId: -1 }); // this needs to be different than initialValues to trigger Formik's reset
    } else if (!e) {
      setImageSrc('');
      setTemplateId(undefined);
      setFormValues(initialValues);
    } else {
      const { templateId } = e;
      setTemplateId(templateId);
    }
  };
  const handleCheckboxChange = (
    isChecked: boolean,
    formVals: Partial<FormData>,
  ): void => {
    if (isChecked) {
      setFormValues({ ...formVals, ...authoredByPaperlessValues });
      setIsAuthoredByPaperlessParts(true);
    } else {
      setFormValues({ ...formVals, ...resetAuthoredByPaperlessValues });
      setIsAuthoredByPaperlessParts(false);
    }
  };

  const handleClose = () => {
    dispatch(managedIntegrationDrawer.close());
  };

  const onFileSelected = (file) => {
    setFileBlob(file);
  };

  const validateEmail = (email: string) => {
    return email.match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );
  };

  const validateAwsKey = (key: string | undefined) =>
    !!key && key.match(/^\S+$/);

  const validateIccSetupCredentials = (
    isIccEnabled: boolean | undefined,
    awsCustomerAccessKey: string | undefined,
    awsCustomerSecretKey: string | undefined,
  ) => {
    if (isIccEnabled) {
      const fieldsAreBothEmptyOrFilled =
        !awsCustomerAccessKey === !awsCustomerSecretKey;
      if (!fieldsAreBothEmptyOrFilled) return false;
      if (!awsCustomerAccessKey && !awsCustomerSecretKey) return true;
      return (
        validateAwsKey(awsCustomerAccessKey) &&
        validateAwsKey(awsCustomerSecretKey)
      );
    }
    return !awsCustomerAccessKey && !awsCustomerSecretKey;
  };

  const isSubmitButtonEnabled = (values: Partial<FormData>) => {
    if (!isAuthoredByPaperlessParts) {
      return (
        values.name?.length &&
        values.category?.length &&
        values.description?.length &&
        values.author?.length &&
        values.supportContact?.length &&
        validateEmail(values.supportContact) &&
        validateIccSetupCredentials(
          values.isIccEnabled,
          values.awsCustomerAccessKey,
          values.awsCustomerSecretKey,
        )
      );
    }

    return (
      values.name?.length &&
      values.category?.length &&
      values.description?.length &&
      validateIccSetupCredentials(
        values.isIccEnabled,
        values.awsCustomerAccessKey,
        values.awsCustomerSecretKey,
      )
    );
  };

  useEffect(() => {
    if (template?.isSuccess && !isEditing) {
      const { data } = template;
      const thumbnail = data?.logo?.thumbnail;

      data.integrationTemplateId = data.id;
      data.integratedSoftwareName = data.name;

      if (data.isIccEnabled) {
        data.awsCustomerAccessKey = '';
        data.awsCustomerSecretKey = '';
        data.releaseChannel = DEFAULT_MANAGED_INTEGRATION_RELEASE_CHANNEL;
      }
      setFormValues(data);
      setImageSrc(thumbnail);
      setIsAuthoredByPaperlessParts(data.developedByPaperlessParts);
    }
  }, [template, isEditing]);

  useEffect(() => {
    if (managedIntegrationData) {
      const {
        author,
        category,
        name,
        description,
        supportContact,
        developedByPaperlessParts,
        showStatusIndicator,
        isPausable,
        isIccEnabled,
        isIccCompatible,
        releaseChannel,
      } = managedIntegrationData;
      const thumbnail = managedIntegrationData.logo?.thumbnail;
      setImageSrc(thumbnail);
      setIsAuthoredByPaperlessParts(
        managedIntegrationData.developedByPaperlessParts,
      );
      setFormValues({
        author,
        category,
        name,
        description,
        supportContact,
        developedByPaperlessParts,
        showStatusIndicator,
        isPausable,
        isIccEnabled,
        isIccCompatible,
        releaseChannel:
          releaseChannel === 'stable' || releaseChannel === 'dev'
            ? releaseChannel
            : '',
      });
    }
  }, [managedIntegrationData]);

  const handleChange = (e) => {
    if (!validateEmail(e.target.value)) {
      setEmailInvalid(true);
    } else {
      setEmailInvalid(false);
    }
  };

  if (createLogoIsLoading || editIsLoading)
    return (
      <Center h={'100%'}>
        <Spinner />
      </Center>
    );

  const onMutationError = (error: Error) => {
    handleClose();
    notifyError(
      isEditing
        ? 'Unable to update integration settings'
        : 'Unable to create integration',
      error.message,
    );
  };

  const onMutationSuccess = (data: Partial<ManagedIntegrationTemplate>) => {
    handleClose();
    notifySuccess(
      isEditing ? 'Updated integration settings' : 'Created integration',
      isEditing
        ? `"${data.name}" integration settings were updated.`
        : `Find "${data.name}" in your integration settings.`,
    );
  };
  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={formValues}
        onSubmit={async (values: Partial<FormData>) => {
          delete values.integrationTemplateId;
          if (isEditing) {
            delete values.releaseChannel;
            if (fileBlob) {
              const formData = new FormData();
              formData.append('file', fileBlob);
              createLogo(formData).then((res) => {
                if (isAuthoredByPaperlessParts) {
                  delete values.author;
                  delete values.supportContact;
                }
                const { id } = res;
                values.logoId = id;
                values.category = values.category.toLowerCase();
                values.uuid = managedIntegrationUuid;
                editTemplate(
                  { body: values, uuid: values.uuid },
                  {
                    onError(error) {
                      onMutationError(error);
                    },
                    onSuccess(data) {
                      onMutationSuccess(data);
                    },
                  },
                );
              });
            } else {
              // If its authored by PP we don't want to send this in the payload
              if (managedIntegrationData?.logo?.id && !imageSrc) {
                const logoId = managedIntegrationData?.logo?.id;
                deleteLogo(logoId);
              }

              if (isAuthoredByPaperlessParts) {
                delete values.author;
                delete values.supportContact;
              }
              // Send in the patch
              values.category = values.category.toLowerCase();
              values.uuid = managedIntegrationUuid;
              editTemplate(
                { body: values, uuid: values.uuid },
                {
                  onError(error) {
                    onMutationError(error);
                  },
                  onSuccess(data) {
                    onMutationSuccess(data);
                  },
                },
              );
            }
          } else if (templateId) {
            const {
              isIccEnabled,
              awsCustomerAccessKey,
              awsCustomerSecretKey,
              releaseChannel,
            } = values;
            const payload: Partial<FormData> = {
              integrationTemplateId: templateId,
              ...(isIccEnabled && { isIccEnabled }),
              ...(isIccEnabled && {
                releaseChannel:
                  releaseChannel === 'stable' || releaseChannel === 'dev'
                    ? releaseChannel
                    : DEFAULT_MANAGED_INTEGRATION_RELEASE_CHANNEL,
              }),
            };
            if (isIccEnabled) {
              const key = awsCustomerAccessKey?.trim();
              const secret = awsCustomerSecretKey?.trim();
              if (key && secret) {
                payload.awsCustomerAccessKey = key;
                payload.awsCustomerSecretKey = secret;
              }
            }
            createTemplate(payload, {
              onError(error) {
                onMutationError(error);
              },
              onSuccess(data) {
                onMutationSuccess(data);
              },
            });
          } else {
            if (isAuthoredByPaperlessParts) {
              delete values.author;
              delete values.supportContact;
            }
            if (!values.isIccEnabled) {
              delete values.awsCustomerAccessKey;
              delete values.awsCustomerSecretKey;
              delete values.releaseChannel;
            } else {
              const key = values.awsCustomerAccessKey?.trim();
              const secret = values.awsCustomerSecretKey?.trim();
              if (key && secret) {
                values.awsCustomerAccessKey = key;
                values.awsCustomerSecretKey = secret;
              } else {
                delete values.awsCustomerAccessKey;
                delete values.awsCustomerSecretKey;
              }
              values.releaseChannel =
                values.releaseChannel === 'stable' ||
                values.releaseChannel === 'dev'
                  ? values.releaseChannel
                  : DEFAULT_MANAGED_INTEGRATION_RELEASE_CHANNEL;
            }
            values.category = values.category.toLowerCase();
            values.integratedSoftwareName = values.name;

            if (fileBlob) {
              const formData = new FormData();
              formData.append('file', fileBlob);
              const { id } = await createLogo(formData);
              values.logoId = id;
            }

            createTemplate(values, {
              onError(error) {
                onMutationError(error);
              },
              onSuccess(data) {
                onMutationSuccess(data);
              },
            });
          }
        }}
      >
        {(props) => {
          return (
            <Box
              as={Form}
              display={'flex'}
              flexDir={'column'}
              alignSelf={'stretch'}
              flexGrow={1}
              minH={0}
            >
              <DrawerBody
                display={'flex'}
                flexDir={'column'}
                alignSelf={'stretch'}
                flexGrow={1}
                minH={0}
                overflowY={'auto'}
                gap={2}
                paddingBottom={3}
              >
                {isSuperUser && !isEditing && (
                  <>
                    <Field name="integrationTemplateId">
                      {() => {
                        return (
                          <FormControl isRequired={true}>
                            <FormLabel htmlFor="integrationTemplateId">
                              TEMPLATE
                            </FormLabel>
                            <IntegrationTemplateSelector
                              onChange={handleTemplateChange}
                            />
                          </FormControl>
                        );
                      }}
                    </Field>
                    <Divider />
                  </>
                )}
                <Field name="name">
                  {() => {
                    return (
                      <FormControl isRequired={true}>
                        <FormLabel htmlFor="name">SOFTWARE NAME</FormLabel>
                        <Input
                          isDisabled={fieldsDisabled}
                          onChange={props.handleChange}
                          id="name"
                          value={props.values.name}
                          name="name"
                          placeholder="Enter the name of software you are integrating with"
                          maxLength={250}
                        />
                      </FormControl>
                    );
                  }}
                </Field>
                <Field name="category">
                  {({ field }) => {
                    const val =
                      (field?.value?.length && field?.value === 'crm') ||
                      field?.value === 'erp'
                        ? {
                            label: field.value.toUpperCase(),
                            value: field.value,
                          }
                        : field?.value?.length
                        ? {
                            label:
                              field.value.charAt(0).toUpperCase() +
                              field.value.slice(1),
                            value: field.value,
                          }
                        : '';
                    return (
                      <FormControl isRequired={true}>
                        <FormLabel htmlFor="category">
                          Software category
                        </FormLabel>
                        <Box
                          _hover={
                            templateId
                              ? { cursor: 'not-allowed' }
                              : { cursor: 'default' }
                          }
                        >
                          <Select
                            isClearable={false}
                            isDisabled={fieldsDisabled}
                            value={val}
                            options={categoryOptions}
                            onChange={(e: { label: string; value: string }) => {
                              if (!e) {
                                props.setFieldValue(field.name, '');
                              } else {
                                props.setFieldValue(field.name, e.value);
                              }
                            }}
                            placeholder="Select a category"
                          />
                        </Box>
                      </FormControl>
                    );
                  }}
                </Field>
                <Field name="description">
                  {() => {
                    return (
                      <FormControl isRequired={true}>
                        <FormLabel htmlFor="description">
                          INTEGRATION description
                        </FormLabel>
                        <Input
                          isDisabled={fieldsDisabled}
                          onBlur={props.handleBlur}
                          onChange={props.handleChange}
                          id="description"
                          value={props.values.description}
                          name="description"
                          placeholder="Describe your integration’s capabilities in one sentence"
                          maxLength={1000}
                        />
                      </FormControl>
                    );
                  }}
                </Field>
                <Field name="logo">
                  {() => {
                    return (
                      <FormControl>
                        <FormLabel htmlFor="logo">INTEGRATION LOGO</FormLabel>
                        <LogoFormControl
                          isFromTemplate={fieldsDisabled}
                          src={imageSrc}
                          onFileCallback={onFileSelected}
                          onImageSelected={setImageSrc}
                        />
                      </FormControl>
                    );
                  }}
                </Field>
                {isSuperUser && (
                  <Field type="checkbox" name="developedByPaperlessParts">
                    {({ field }) => {
                      return (
                        <FormControl>
                          <Checkbox
                            name="developedByPaperlessParts"
                            id="developedByPaperlessParts"
                            isDisabled={fieldsDisabled}
                            isChecked={props.values.developedByPaperlessParts}
                            onChange={() => {
                              props.setFieldValue(
                                field.name,
                                !props.values.developedByPaperlessParts,
                              );
                              handleCheckboxChange(
                                !props.values.developedByPaperlessParts,
                                props.values,
                              );
                            }}
                          >
                            Paperless Parts authorship
                          </Checkbox>
                        </FormControl>
                      );
                    }}
                  </Field>
                )}
                <Field name="author">
                  {() => {
                    return (
                      <FormControl isRequired={true}>
                        <FormLabel htmlFor="author">Author</FormLabel>
                        {isAuthoredByPaperlessParts ? (
                          <Flex
                            cursor={'not-allowed'}
                            backgroundColor={'interaction.surfaceDisabled'}
                            height={4}
                            pl={1}
                            lineHeight={3}
                            align="center"
                            border={'1px solid #ccc'}
                          >
                            <Text size="md" mr={0.25} color={'brand.secondary'}>
                              Paperless Parts
                            </Text>{' '}
                            <PaperlessCubeIcon
                              w={'1.375rem'}
                              h={'1.375rem'}
                              color={'brand.secondary'}
                            />
                          </Flex>
                        ) : (
                          <Input
                            isDisabled={
                              fieldsDisabled || isAuthoredByPaperlessParts
                            }
                            onChange={props.handleChange}
                            value={props.values.author}
                            name="author"
                            id="author"
                            placeholder="Enter a company (if applicable) or individual author name"
                            maxLength={120}
                          />
                        )}
                      </FormControl>
                    );
                  }}
                </Field>
                <Field name="supportContact">
                  {() => {
                    return (
                      <FormControl isInvalid={emailInvalid} isRequired={true}>
                        <FormLabel htmlFor="supportContact">
                          Support Contact
                        </FormLabel>
                        <Input
                          isInvalid={emailInvalid}
                          onChange={(e) => {
                            props.handleChange(e);
                            handleChange(e);
                          }}
                          isDisabled={
                            fieldsDisabled || isAuthoredByPaperlessParts
                          }
                          value={
                            isAuthoredByPaperlessParts
                              ? '(via support drawer)'
                              : props.values.supportContact
                          }
                          name="supportContact"
                          id="supportContact"
                          placeholder="Enter an email for support requests about this integration"
                          maxLength={254}
                        />
                        <FormErrorMessage>
                          Enter a valid email address.
                        </FormErrorMessage>
                      </FormControl>
                    );
                  }}
                </Field>
                <Field type="checkbox" name="showStatusIndicator">
                  {() => (
                    <FormControl>
                      <Checkbox
                        name="showStatusIndicator"
                        id="showStatusIndicator"
                        isDisabled={fieldsDisabled}
                        isChecked={props.values.showStatusIndicator}
                        onChange={props.handleChange}
                      >
                        Show status indicator
                      </Checkbox>
                    </FormControl>
                  )}
                </Field>
                <Field type="checkbox" name="isPausable">
                  {() => (
                    <FormControl>
                      <Checkbox
                        name="isPausable"
                        id="isPausable"
                        isDisabled={fieldsDisabled}
                        isChecked={props.values.isPausable}
                        onChange={props.handleChange}
                      >
                        Allow users to pause and resume the integration
                      </Checkbox>
                    </FormControl>
                  )}
                </Field>
                {isSuperUser && !isEditing && (
                  <VStack align="stretch" spacing={2}>
                    <Field type="checkbox" name="isIccEnabled">
                      {() => (
                        <FormControl>
                          <Checkbox
                            name="isIccEnabled"
                            id="isIccEnabled"
                            isDisabled={!props.values.isIccCompatible}
                            isChecked={props.values.isIccEnabled}
                            onChange={(e) => {
                              props.handleChange(e);
                              if (e.target.checked) {
                                props.setFieldValue(
                                  'releaseChannel',
                                  DEFAULT_MANAGED_INTEGRATION_RELEASE_CHANNEL,
                                );
                              } else {
                                props.setFieldValue('awsCustomerAccessKey', '');
                                props.setFieldValue('awsCustomerSecretKey', '');
                                props.setFieldValue('releaseChannel', '');
                              }
                            }}
                          >
                            Enable Cloud Connector Features
                          </Checkbox>
                        </FormControl>
                      )}
                    </Field>
                    {props.values.isIccEnabled && (
                      <VStack ml={3} align="stretch" spacing={2}>
                        <IccSetupReleaseChannel />
                        <IccSetupAwsCredentialsFields
                          onChange={props.handleChange}
                        />
                      </VStack>
                    )}
                  </VStack>
                )}
              </DrawerBody>
              <DrawerFooter>
                <Flex
                  flexDir={'column'}
                  width={'100%'}
                  height={'max-content'}
                  gap={2}
                >
                  <VStack
                    background={'page.plateLight'}
                    minHeight={'138px'}
                    borderRadius={'4px'}
                    p={2}
                    pt={1.5}
                    align={'flex-start'}
                  >
                    <Text
                      size="sm"
                      textTransform={'uppercase'}
                      color={'feature.secondary'}
                      fontWeight={'bold'}
                      align={'left'}
                    >
                      Preview
                    </Text>
                    <ManagedIntegrationCard
                      imageUrl={
                        imageSrc || MANAGED_INTEGRATION_LOGO_PLACEHOLDER
                      }
                      developedByPaperlessParts={isAuthoredByPaperlessParts}
                      name={props.values.name}
                      author={props.values.author}
                      description={props.values.description}
                    />
                  </VStack>
                  <ButtonGroup>
                    <Button
                      marginLeft={'auto'}
                      onClick={handleClose}
                      variant={'secondary'}
                    >
                      Cancel
                    </Button>
                    {!isEditing ? (
                      <Button
                        isDisabled={!isSubmitButtonEnabled(props.values)}
                        type="submit"
                      >
                        Create Integration
                      </Button>
                    ) : (
                      <Button
                        isDisabled={!isSubmitButtonEnabled(props.values)}
                        type="submit"
                      >
                        Save Integration
                      </Button>
                    )}
                  </ButtonGroup>
                </Flex>
              </DrawerFooter>
            </Box>
          );
        }}
      </Formik>
    </>
  );
};

export default CreateManagedIntegrationTemplateForm;
